import { get as getCookie, getAll as getAllCookies, set as setCookie } from 'es-cookie';
import { deepGetOfType } from 'bam-deep-get';
import { AdobeAnalytics } from '../analytics/adobeAnalytics';
import { initAdobeLaunch } from '../analytics/adobeLaunch';

const OT_COOKIE_KEYS = {
    BOX_CLOSED: 'OptanonAlertBoxClosed',
    CONSENT: 'OptanonConsent'
};

let prevConsent = '';
let loginConsentState = null;

/**
 * Check if OneTrust is active
 * @returns {Boolean} True = OneTrust active
 */
function isOneTrustActive() {
    return (window.Optanon);
}

/**
 * @description Check to see if the user consented to a particular group
 * @param {string} group The groups number
 * @returns {boolean} False if not active.  True if user accepts or OneTrust not present
 */
export function oneTrustIsActiveGroup(group) {
    return (window.OptanonActiveGroups && window.OptanonActiveGroups.match(group));
}

/**
 * @description Check to see if the previous Consent contains any items that were deactivated.
 * If they are found return true to signal a reload is required.
 * @returns {Boolean} If a restart is required due to a previously activated group being disabled.
 */
function checkForReload() {
    const prevActivedGroups = prevConsent.split(',');

    const deactivated = prevActivedGroups.filter(group => {
        if (!group) return false;

        return !(oneTrustIsActiveGroup(group));
    });

    return (deactivated.length);
}

/**
 * @description Add a function to be called when the user updates their OneTrust Consent
 * @param {Function} func = Function to be run when the user updates their consent
 */
export function addOneTrustOnConsentFunction(func) {
    if (!window.OptanonWrapperFunctions) {
        window.OptanonWrapperFunctions = [];
    }

    if (func && typeof func === 'function') {
        window.OptanonWrapperFunctions.push(func);
    }
}

/**
 * @description If there are any OptanonWrapperFunctions functions that were added after OneTrust loaded,
 * this function will re-run the OptanonWrapper method
 * @param {function} onConsentChanged - callback for consent change
 * @param {Object} options callbacks for onload and onerror
 */
export function initalizeOneTrust(onConsentChanged = () => {}, options = { onload: () => {}, onerror: () => {} }) {
    const { OneTrust } = window;

    if (OneTrust) {
        addOneTrustOnConsentFunction(()=>{
            if (!window.OneTrust.loaded) {
                // eslint-disable-next-line new-cap
                window.Optanon.OnConsentChanged(() => {
                    onConsentChanged();
                });
                window.OneTrust.loaded = true;
            }
        });

        const script = document.createElement('script');

        script.type = 'text/javascript';
        script.charset = 'UTF-8';
        script.defer = true;
        script.onerror = () => {
            options.onerror();
        };
        script.onload = () => {
            options.onload();
        };
        script.src = OneTrust.src;
        script.setAttribute('data-document-language', 'true');
        script.setAttribute('data-domain-script', OneTrust.guuid);

        document.head.appendChild(script);

        prevConsent = window.OptanonActiveGroups;

        //Adding in preventDetaults for OT prefernce enter links that go to href="#"
        const thirdPartyLinks = document.querySelectorAll('#onetrust-pc-sdk [href="#"]');

        thirdPartyLinks.forEach(link => {
            link.addEventListener('click', e => {
                e.preventDefault();
            });
        });
    }
}

/**
 * @description Takes the users AccountId and associates that Id with the user consent
 * @param {string} id - The user's D+ account Id or device id
 * @param {bool} isAuth - Login state
 * @param {string} countryCode - Users physical location
 */
export function syncOneTrustConsent(id, isAuth, countryCode) {
    const { OneTrustConsent } = window;

    if (isOneTrustActive()) {
        if (prevConsent === window.OptanonActiveGroups && loginConsentState === isAuth) return;

        let reload = false;

        if (prevConsent) {
            reload = checkForReload();
        }

        prevConsent = window.OptanonActiveGroups;
        loginConsentState = isAuth;

        if (OneTrustConsent) {
            OneTrustConsent.sendConsent(id, isAuth, countryCode, () => {
                if (reload) {
                    const cookies = getAllCookies();

                    for (const name in cookies) {
                        if (!Object.values(OT_COOKIE_KEYS).includes(name)) {
                            setCookie(name, cookies[name], { expires: -1 });
                        }
                    }
                    window.location.reload();
                }
            });
        }
    }
}


/**
 * @description Set OptanonActiveGroups Fallback to be cat 1
 */
export function setFallbackActiveGroup() {
    window.OptanonActiveGroups = ',1,';
}

/**
 * @description If the Onetrust featureConfig is on, this function will be used to get a local version of
 * OptanonActiveGroups.
 * If the OneTrust cookie is already set us that to populate
 * If the fallback cookie is set activate all categories.
 */
export function activateAllCategories() {
    if (window.dssMlpConfigs.regionConfig.featureConfig.enableCMP) {
        setFallbackActiveGroup();
    } else {
        window.OptanonActiveGroups = ',1,2,3,4,';
    }
}


/**
 * @description If the current user is a kid's profile do not show the consent banner.  The next time
 * the user is on an adult profile, show the banner.
 * @param {object} profile The current active profile
 */
export function shouldBlockBanner(profile) {
    const closedExists = getCookie(OT_COOKIE_KEYS.BOX_CLOSED);
    const kidsModeEnabled = deepGetOfType(profile, 'attributes.kidsModeEnabled', Boolean, false);

    if (!closedExists && kidsModeEnabled) {
        setCookie(OT_COOKIE_KEYS.BOX_CLOSED, 'kidsAccountBlock');
    } else if (closedExists === 'kidsAccountBlock' && !kidsModeEnabled) {
        setCookie(OT_COOKIE_KEYS.BOX_CLOSED, 'kidsAccountBlock', { expires: -1 });
    }
}

/**
 * @description When the user accepts Group 4 cookies, load both Adobe Analytics and Adobe Launch
 */
export function loadAdobeSuites(cmpDisabled = false) {
    if (oneTrustIsActiveGroup('2')) {
        // eslint-disable-next-line new-cap

        if (cmpDisabled) {
            AdobeAnalytics.loadScript(() => {
                initAdobeLaunch(cmpDisabled);
            });
        } else {
            window.Optanon.InsertScript(
                `${window.dssMlpConfigs.staticRoot}/scripts/analytics_script_bundle.js`,
                'head',
                () => {
                    AdobeAnalytics.track(new Event('load'));
                },
                null,
                '4'
            );

            if (window.dssMlpConfigs.adobeLaunchUrl) {
                // eslint-disable-next-line new-cap
                window.Optanon.InsertScript(window.dssMlpConfigs.adobeLaunchUrl, 'head', () => {
                    initAdobeLaunch(cmpDisabled);
                }, null, '4');
            }
        }
    }
}
